import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { GetProfileRequest } from "@/dtos/Unipile.dto";

export interface LinkedInProfileParams {
  identifier: string;
  accountId?: string;
  linkedinSections?: string[] | string;
  linkedinApi?: "recruiter" | "sales_navigator";
  notify?: boolean;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useLinkedInProfile(
  params: Ref<LinkedInProfileParams | null> | LinkedInProfileParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["linkedin-profile", "disabled"];
    return [
      "linkedin-profile",
      p.identifier,
      p.accountId || "",
      Array.isArray(p.linkedinSections) ? p.linkedinSections.join(",") : p.linkedinSections || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.identifier;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const requestParams: GetProfileRequest = {};
      if (p.accountId) requestParams.account_id = p.accountId;
      if (p.linkedinSections) requestParams.linkedin_sections = p.linkedinSections;
      if (p.linkedinApi) requestParams.linkedin_api = p.linkedinApi;
      if (p.notify !== undefined) requestParams.notify = p.notify;

      const response = await Unipile.getProfile(p.identifier, requestParams);

      if (response.data?.status) {
        return response.data.data;
      }

      throw new Error("Failed to load LinkedIn profile");
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
